import { FC } from 'react';
import type { AdminDocumentListItem } from '@libs/types';
import { Box, Button, Dialog, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { BracketsCurlyIcon, XIcon } from '@phosphor-icons/react';

import { adminColors } from './adminTheme';

interface Props {
  open: boolean;
  onClose: () => void;
  doc: AdminDocumentListItem | null;
}

/**
 * Read-only viewer for a document's metadata. Renders exactly the fields the
 * admin documents endpoint returns (no fabricated classification blob).
 */
export const MetadataDialog: FC<Props> = observer(({ open, onClose, doc }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();

  const json = doc
    ? JSON.stringify(
        {
          id: doc.id,
          fileName: doc.fileName,
          source: doc.source,
          classificationStatus: doc.classificationStatus,
          confidence: doc.confidence,
          fileSize: doc.fileSize,
          contentType: doc.contentType,
        },
        null,
        2,
      )
    : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.625,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.5,
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BracketsCurlyIcon size={20} color={c.violet} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: c.ink }}>
            {t('app.admin.metadata.title')}
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              color: c.faint,
              mt: 0.125,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {doc?.fileName}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}
        >
          <XIcon size={16} />
        </Button>
      </Box>

      <Box sx={{ px: 3, py: 2.75 }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            backgroundColor: '#0F1117',
            color: '#E4E4E7',
            borderRadius: 3,
            p: 2.5,
            fontSize: 12,
            lineHeight: 1.65,
            fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {json}
        </Box>
      </Box>
    </Dialog>
  );
});
